import { Close, Delete } from "@mui/icons-material";
import { Autocomplete, Box, Button, Checkbox, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import Dropzone from "react-dropzone";
import { toast } from "react-hot-toast";
import { sanitizeFileName, getCurrentTime } from "../utils/assistantUtils";
import FilingPackageXFA from "./FilingPackageXFA";
import { axios_tenant } from "../../../../axios";
import PropTypes from "prop-types";

const FILING_TYPE = [
  { name: "Continuation", value: "continuation" },
  { name: "Continuation-In-Part", value: "continuationinpart" },
  { name: "Non-Provisional", value: "nonprovisional" },
  { name: "Divisional", value: "divisional" },
  { name: "371 Application", value: "371application" },
  { name: "By-Pass Continuation", value: "bypasscontinuation" },
];

const ENTITY_STATUS = [
  { name: "Large", value: "large" },
  { name: "Small", value: "small" },
  { name: "Micro", value: "micro" },
];

const ACCEPTED_FILE_TYPES = "application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const cleanData = (data) => {
  if (Array.isArray(data)) return data.map((v) => (v === "" ? null : cleanData(v)));
  if (data && typeof data === "object") return Object.fromEntries(Object.entries(data).map(([k, v]) => [k, cleanData(v)]));
  return data === "" ? null : data;
};

// Regexes
const pctStrictRegex = /^PCT\/[A-Z]{2}\d{4}\/\d{6}$/;
const pctNumRegex = /^PCT\/([A-Z]{2})(?:\d{2}|\d{4})\/(\d{1,6})$/;
const usFormatRegex = /^(?:\d{2}\/\d{3},\d{3}|\d{2}\/\d{6}|\d{8})$/;

function validateParentAppNumber(value = "", filingType = "") {
  const trimmed = value.trim();
  if (!trimmed || !filingType) return { isValid: false, pctError: false, usError: false };

  if (filingType === "371application" || filingType === "bypasscontinuation") {
    const ok = pctStrictRegex.test(trimmed.toUpperCase()) || pctNumRegex.test(trimmed.toUpperCase());
    return { isValid: ok, pctError: !ok, usError: false };
  }
  const rawDigits = trimmed.replace(/\D/g, "");
  const ok = usFormatRegex.test(trimmed) || rawDigits.length === 8;
  return { isValid: ok, pctError: false, usError: !ok };
}

const UsFilingModal = ({ setUsFilingIntent, handleModuleSubmit, handleStop, setMessages, originatingRequestId, requestId }) => {
  const subdomain = window.location.hostname.split(".")[0];

  const PROCOPIO = process.env.REACT_APP_PROCOPIO;

  const [activeTab, setActiveTab] = useState(0);
  const [usFilingFiles, setUsFilingFiles] = useState({
    specification: null,
    drawings: null,
    gpoa: null,
    amendedSpecification: null,
  });
  const [adsFormData, setAdsFormData] = useState({});
  const [resetAds, setResetAds] = useState(false);
  const [usFilingData, setUsFilingData] = useState({
    docketNumber: "",
    parentApplicationNumber: "",
    filingType: "",
    attorneyName: "",
    attorneyReferenceNumber: "",
    signatureRequired: true,
    customerNumber: "",
    idsRequired: true,
    entityStatus: "",
    clientInventionTitle: "",
  });
  const [isRequiredField, setIsRequiredField] = useState(false);
  const [hasVisitedTab1, setHasVisitedTab1] = useState(false);
  const [pctFormatError, setPctFormatError] = useState(false);
  const [usFormatError, setUsFormatError] = useState(false);
  const [attorneys, setAttorneys] = useState([]);
  const [clientEmail, setClientEmail] = useState("");
  const [isNewAttorney, setIsNewAttorney] = useState(false);

  useEffect(() => {
    setUsFilingData((prev) => ({
      ...prev,
      signatureRequired: subdomain?.toLowerCase() === PROCOPIO?.toLowerCase() ? false : true,
    }));
  }, [subdomain]);

  useEffect(() => {
    const { isValid, pctError, usError } = validateParentAppNumber(usFilingData.parentApplicationNumber, usFilingData.filingType);

    setPctFormatError((usFilingData.filingType === "371application" || usFilingData.filingType === "bypasscontinuation") && !!usFilingData.parentApplicationNumber && !isValid && pctError);
    setUsFormatError(usFilingData.filingType !== "371application" && usFilingData.filingType !== "bypasscontinuation" && !!usFilingData.parentApplicationNumber && !isValid && usError);
  }, [usFilingData.parentApplicationNumber, usFilingData.filingType]);

  useEffect(() => {
    const getClientData = async () => {
      try {
        const response = await axios_tenant.get("/trexo-assistant/getAppgenClientData");
        const defaultEmail = response.data?.find((item) => item.default_values && item.default_values.correspondenceEmail)?.default_values?.correspondenceEmail || "";
        setClientEmail(defaultEmail);

        const defaultAttorneys = response.data?.find((item) => item.default_values && item.default_values.attorneys)?.default_values?.attorneys || [];
        setAttorneys(defaultAttorneys);
      } catch (error) { }
    };

    getClientData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;

    const finalValue = type === "checkbox" ? checked : value;

    setUsFilingData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    if (value) setIsRequiredField(false);
  };

  async function validateStep1({ checkFiles = false } = {}) {
    const { docketNumber, parentApplicationNumber, filingType } = usFilingData;

    if (!parentApplicationNumber || !filingType || !docketNumber) {
      setIsRequiredField(true);
      toast.error("Required fields missing!", { duration: 1500 });
      return false;
    }

    const { isValid } = validateParentAppNumber(parentApplicationNumber, filingType);
    if (!isValid) {
      toast.error("Invalid parent application number.", { duration: 1500 });
      return false;
    }

    if (pctFormatError || usFormatError) return false; // guard (should already be covered)

    return true;
  }

  const handleTabChange = async (_, newValue) => {
    if (newValue === 1) {
      const ok = await validateStep1();
      if (!ok) return;

      if (usFilingFiles.amendedSpecification && !usFilingFiles.specification) {
        toast.error("Please upload the original copy of the specification.", { duration: 1500 });
        return;
      }

      setHasVisitedTab1(true);
    }
    setActiveTab(newValue);
  };

  const handleNext = async () => {
    const ok = await validateStep1({ checkFiles: true });

    if (usFilingFiles.amendedSpecification && !usFilingFiles.specification) {
      toast.error("Please upload the original copy of the specification", { duration: 1500 });
      return;
    }

    if (ok) {
      setHasVisitedTab1(true);
      setActiveTab(1);
    }
  };

  const handleTabReset = () => {
    if (activeTab === 0) {
      setUsFilingData({
        docketNumber: "",
        parentApplicationNumber: "",
        filingType: "",
        attorneyName: "",
        attorneyReferenceNumber: "",
        signatureRequired: subdomain?.toLowerCase() === PROCOPIO?.toLowerCase() ? false : true,
        customerNumber: "",
        idsRequired: true,
        entityStatus: "",
        clientInventionTitle: "",
      });
      setUsFilingFiles({
        specification: null,
        drawings: null,
        gpoa: null,
        amendedSpecification: null,
      });
      setHasVisitedTab1(false);
      setAdsFormData({});
      setResetAds(true);
      setIsNewAttorney(false);
    } else {
      setAdsFormData({});
      setResetAds(true);
    }
    setPctFormatError(false);
    setUsFormatError(false);
    setIsRequiredField(false);
  };

  const handleSubmit = async () => {
    const ok = await validateStep1();
    if (!ok) return;

    let cleanedParentAppNum = usFilingData.parentApplicationNumber.toUpperCase();
    if (usFilingData.filingType !== "371application" && usFilingData.filingType !== "bypasscontinuation") {
      cleanedParentAppNum = cleanedParentAppNum.replace(/\D/g, "");
    }
    const userInputData = { ...usFilingData, parentApplicationNumber: cleanedParentAppNum, ...adsFormData };
    const cleanedUserInputData = cleanData(userInputData);
    const formattedString = JSON.stringify(cleanedUserInputData)
      .replace(/"([^"]+)":/g, "$1:")
      .replace(/null,\/\//g, "null,//");

    const typeName = FILING_TYPE.find((t) => t.value === usFilingData.filingType)?.name || "";
    let textInput = "Docket Number: " + usFilingData.docketNumber + "<br />Parent Application Number: " + usFilingData.parentApplicationNumber + "<br />Filing Type: " + typeName + "<br />Attorney Name: " + usFilingData.attorneyName + "<br />Attorney Registration Number: " + usFilingData.attorneyReferenceNumber;

    if (usFilingData.customerNumber) textInput += "<br />Customer Number: " + usFilingData.customerNumber;

    if (usFilingData.entityStatus) textInput += "<br />Entity Status: " + ENTITY_STATUS.find((t) => t.value === usFilingData.entityStatus)?.name || "";

    if (usFilingData.clientInventionTitle) textInput += "<br />Client Invention Title: " + usFilingData.clientInventionTitle;

    const usFilingFullData = {
      usFilingTextData: formattedString,
      uploadedFiles: Object.entries(usFilingFiles)
        .filter(([_, file]) => !!file)
        .map(([role, file]) => {
          const renamed = new File([file], `${role}::${file.name}`, { type: file.type });
          return renamed;
        }),
    };

    setUsFilingIntent(false);

    await handleModuleSubmit({ textInput, largeSlotData: formattedString, originatingRequestId, requestId, usFilingFullData });
  };

  return (
    <Box sx={{ width: "100%", height: "92vh", background: "#fefefe", display: "flex", flexDirection: "column", padding: "1.5rem", gap: "1rem" }}>
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Button
          onClick={() => {
            setUsFilingIntent(false);
            handleStop();
            const time = getCurrentTime();
            setMessages((prev) => [...prev, { from: "assistant", content: "Request cancelled.", time }]);
          }}
          sx={{ width: "fit-content", ":hover": { background: "transparent" } }}
        >
          <Close />
        </Button>
      </Box>

      <Box>
        <Typography fontSize={"1rem"} color={"black"}>
          Please fill the below details to continue filing package
        </Typography>
      </Box>

      <div>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ "& .MuiTabs-indicator": { backgroundColor: "#005cb9" } }}>
          <Tab label="US Filing Details" sx={{ color: activeTab === 0 ? "#005cb9" : "gray", "&.Mui-selected": { color: "#005cb9" } }} />
          <Tab label="Ads Page Details" disabled={!hasVisitedTab1} sx={{ color: activeTab === 1 ? "#005cb9" : "gray", "&.Mui-selected": { color: "#005cb9" }, "&.Mui-disabled": { pointerEvents: "auto", cursor: "not-allowed" } }} />
        </Tabs>

        <Box sx={{ paddingY: 3 }}>
          {activeTab === 0 && (
            <Box sx={{ height: "55vh", overflowY: "scroll", padding: "1rem" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
                <TextField label="Docket Number" name="docketNumber" error={isRequiredField} value={usFilingData.docketNumber} required onChange={handleInputChange} sx={{ "& label.Mui-focused": { color: "#005cb9" }, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#005cb9" } } }} />

                <FormControl sx={{ "& label.Mui-focused": { color: "#005cb9" } }} error={isRequiredField}>
                  <InputLabel id="filing-type">Filing Type *</InputLabel>
                  <Select name="filingType" value={usFilingData.filingType} onChange={handleInputChange} labelId="filing-type" label="Filing Type" sx={{ "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#005cb9" } }} required>
                    {FILING_TYPE.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box width={"100%"}>
                  <TextField error={isRequiredField || pctFormatError || usFormatError} sx={{ width: "100%", "& label.Mui-focused": { color: "#005cb9" }, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#005cb9" } } }} label="Parent Application Number" name="parentApplicationNumber" value={usFilingData.parentApplicationNumber} required onChange={handleInputChange} />
                  {pctFormatError && <FormHelperText error>Enter the correct PCT format: PCT/XXYYYY/XXXXXX</FormHelperText>}
                  {usFormatError && <FormHelperText error>Correct number format is XX/XXX,XXX, XXXXXXXX, XX/XXXXXX</FormHelperText>}
                </Box>
                {subdomain?.toLowerCase() === PROCOPIO?.toLowerCase() ? (
                  <>
                    <Box width="100%">
                      <Autocomplete
                        freeSolo
                        options={attorneys}
                        value={attorneys.find((attorney) => attorney.name.toLowerCase() === usFilingData.attorneyName?.toLowerCase()) || usFilingData.attorneyName || null}
                        inputValue={usFilingData.attorneyName || ""}
                        getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                        filterOptions={(options, { inputValue }) => {
                          const searchValue = inputValue.toLowerCase().trim();

                          if (!searchValue) {
                            return options;
                          }

                          return options.filter((attorney) => attorney.name.toLowerCase().includes(searchValue));
                        }}
                        onInputChange={(event, value, reason) => {
                          if (reason === "clear") {
                            setUsFilingData((prev) => ({
                              ...prev,
                              attorneyName: "",
                              attorneyReferenceNumber: "",
                            }));

                            setIsNewAttorney(false);
                            return;
                          }

                          if (reason !== "input") {
                            return;
                          }

                          const trimmedValue = value.trim();

                          const matchingAttorney = attorneys.find((attorney) => attorney.name.toLowerCase() === trimmedValue.toLowerCase());

                          setUsFilingData((prev) => ({
                            ...prev,
                            attorneyName: value,
                            attorneyReferenceNumber: matchingAttorney ? matchingAttorney.registrationNumber : "",
                          }));

                          setIsNewAttorney(trimmedValue.length > 0 && !matchingAttorney);
                        }}
                        onChange={(event, value) => {
                          if (value === null) {
                            setUsFilingData((prev) => ({
                              ...prev,
                              attorneyName: "",
                              attorneyReferenceNumber: "",
                            }));

                            setIsNewAttorney(false);
                            return;
                          }

                          if (typeof value !== "string") {
                            setUsFilingData((prev) => ({
                              ...prev,
                              attorneyName: value.name,
                              attorneyReferenceNumber: value.registrationNumber,
                            }));

                            setIsNewAttorney(false);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Attorney Name"
                            sx={{
                              "& label.Mui-focused": {
                                color: "#005cb9",
                              },
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": {
                                  borderColor: "#005cb9",
                                },
                              },
                            }}
                          />
                        )}
                      />

                      {isNewAttorney && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          This is a new attorney and is not available in the list. Please enter the Attorney Registration Number manually.
                        </FormHelperText>
                      )}
                    </Box>
                    <TextField
                      sx={{
                        "& label.Mui-focused": {
                          color: "#005cb9",
                        },
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#005cb9",
                          },
                        },
                      }}
                      label="Attorney Registration Number"
                      name="attorneyReferenceNumber"
                      value={usFilingData.attorneyReferenceNumber}
                      disabled={!isNewAttorney}
                      onChange={handleInputChange}
                    />
                  </>
                ) : (
                  <>
                    <TextField sx={{ "& label.Mui-focused": { color: "#005cb9" }, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#005cb9" } } }} label="Attorney Name" name="attorneyName" value={usFilingData.attorneyName} onChange={handleInputChange} />

                    <TextField sx={{ "& label.Mui-focused": { color: "#005cb9" }, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#005cb9" } } }} label="Attorney Registration Number" name="attorneyReferenceNumber" value={usFilingData.attorneyReferenceNumber} onChange={handleInputChange} />
                  </>
                )}
                {(usFilingData.filingType === "371application" || usFilingData.filingType === "bypasscontinuation") && (
                  <>
                    <TextField sx={{ "& label.Mui-focused": { color: "#005cb9" }, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#005cb9" } } }} label="Customer Number" name="customerNumber" value={usFilingData.customerNumber} onChange={handleInputChange} />

                    <FormControl sx={{ "& label.Mui-focused": { color: "#005cb9" } }}>
                      <InputLabel id="entity-status">Entity Status</InputLabel>
                      <Select name="entityStatus" value={usFilingData.entityStatus} onChange={handleInputChange} labelId="entity-status" label="Entity Status" sx={{ "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#005cb9" } }}>
                        {ENTITY_STATUS.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                {["371application", "bypasscontinuation", "continuationinpart"].includes(usFilingData.filingType) && <TextField sx={{ "& label.Mui-focused": { color: "#005cb9" }, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#005cb9" } } }} label="Invention Title" name="clientInventionTitle" value={usFilingData.clientInventionTitle} onChange={handleInputChange} />}
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ mb: 1, mt: 2 }}>
                  <Tooltip title="Select this option if you want the Initial IDS to be generated." placement="right" arrow>
                    <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <Checkbox
                        name="idsRequired"
                        checked={usFilingData.idsRequired}
                        onChange={handleInputChange}
                        sx={{
                          "&.Mui-checked": { color: "#005cb9" },
                          "& .MuiSvgIcon-root": { color: "#005cb9" },
                        }}
                      />
                      <span>Generate Initial IDS</span>
                    </Box>
                  </Tooltip>
                </Box>
                {subdomain?.toLowerCase() !== PROCOPIO?.toLowerCase() && (
                  <Box sx={{ mb: 1, mt: 2 }}>
                    <Tooltip title="Unchecking this option will generate the package without the attorney signature." placement="right" arrow>
                      <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                        <Checkbox name="signatureRequired" checked={usFilingData.signatureRequired} onChange={handleInputChange} sx={{ "&.Mui-checked": { color: "#005cb9" }, "& .MuiSvgIcon-root": { color: "#005cb9" } }} />
                        <span>Add attorney signatures</span>
                      </Box>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              <Box>
                <Typography fontSize={"1.5rem"} sx={{ mb: 1, mt: 4 }}>
                  Upload Files
                </Typography>
                <Typography fontSize={"0.9rem"} color={"#808080"} sx={{ mb: 2 }}>
                  It is not mandatory to upload these files
                </Typography>

                <Grid container spacing={2}>
                  {[
                    { key: "specification", label: "Application as Filed or Specification" },
                    { key: "drawings", label: "Drawings" },
                    { key: "gpoa", label: "GPOA" },
                    { key: "amendedSpecification", label: "Complete specifications with Amendments" },
                  ]
                    .filter((item) => {
                      if (item.key === "amendedSpecification" && usFilingData.filingType !== "371application") {
                        return false;
                      }
                      return true;
                    })
                    .map(({ key, label }) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography fontSize={"0.9rem"} sx={{ mb: 1 }}>
                          {label}
                        </Typography>
                        <Dropzone
                          multiple={false}
                          onDrop={(acceptedFiles, rejectedFiles) => {
                            if (rejectedFiles.length > 0) {
                              rejectedFiles.forEach((file) => {
                                toast.error(`Only ${key === "gpoa" ? "PDF" : "DOCX"} files are allowed for this section`, { duration: 2000 });
                              });
                              return;
                            }

                            if (acceptedFiles.length > 0) {
                              const file = acceptedFiles[0];
                              setUsFilingFiles((prev) => ({
                                ...prev,
                                [key]: new File([file], sanitizeFileName(file.name), {
                                  type: file.type,
                                  lastModified: file.lastModified,
                                }),
                              }));
                            }
                          }}
                          accept={key === "gpoa" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document"}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div
                              {...getRootProps()}
                              style={{
                                width: "100%",
                                height: "4rem",
                                border: "2px dashed lightgray",
                                borderRadius: "10px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                            >
                              <input {...getInputProps()} />
                              {usFilingFiles[key] ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "90%",
                                  }}
                                >
                                  <Box sx={{ flex: 1, textAlign: "center" }}>
                                    <span>{usFilingFiles[key].name}</span>
                                  </Box>

                                  <Delete
                                    sx={{
                                      color: "black",
                                      fontSize: "1.1rem",
                                      cursor: "pointer",
                                      ml: 1,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setUsFilingFiles((prev) => ({
                                        ...prev,
                                        [key]: null,
                                      }));
                                    }}
                                  />
                                </Box>
                              ) : (
                                <span style={{ fontSize: "0.9rem", color: "gray" }}>Click or drag file here ({key === "gpoa" ? "pdf" : "docx"} only)</span>
                              )}
                            </div>
                          )}
                        </Dropzone>
                        {key === "specification" && usFilingFiles["amendedSpecification"] && !usFilingFiles["specification"] && (
                          <Typography fontSize={"0.8rem"} color="red" textAlign={"right"} sx={{ mt: 1 }}>
                            Please upload the original copy of the specification
                          </Typography>
                        )}
                      </Grid>
                    ))}
                </Grid>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ overflowY: "scroll", height: "55vh" }}>
              <FilingPackageXFA handleAdsFormDataChange={setAdsFormData} resetAds={resetAds} setResetAds={setResetAds} correspondenceEmail={clientEmail} />
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" sx={{ color: "#005cb9", borderColor: "#005cb9", textTransform: "none" }} onClick={handleTabReset}>
            Reset
          </Button>

          {activeTab === 0 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              sx={{
                background: "#005cb9",
                borderColor: "#005cb9",
                textTransform: "none",
                "&:hover": { background: "#005cb9" },
                "&.Mui-disabled": {
                  cursor: "not-allowed",
                  pointerEvents: "auto",
                  "&:hover": { background: "lightgray" },
                },
              }}
              disabled={!usFilingData.parentApplicationNumber || !usFilingData.filingType || !usFilingData.docketNumber || (usFilingFiles.amendedSpecification && !usFilingFiles.specification)}
            >
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} variant="contained" sx={{ background: "#005cb9", borderColor: "#005cb9", textTransform: "none", "&:hover": { background: "#005cb9" } }}>
              Submit
            </Button>
          )}
        </Box>
      </div>
    </Box>
  );
};

UsFilingModal.propTypes = {
  setUsFilingIntent: PropTypes.func,
  handleModuleSubmit: PropTypes.func,
  handleStop: PropTypes.func,
  setMessages: PropTypes.func,
  originatingRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default UsFilingModal;
